import { Component, OnInit, Output, EventEmitter, Input, AfterViewInit, ViewChild, ElementRef  } from '@angular/core';
import { DataService } from '../../services/data.service';
import { GameState, StoreType } from '../../interfaces/game.interface';

interface ShopDisplay {
  id: string;
  name: string;
  type: StoreType;
  top: number;
  left: number;
  active: boolean;
  yieldStatus: string;
}

@Component({
  selector: 'app-district-page',
  templateUrl: './district-page.component.html',
  styleUrls: ['./district-page.component.scss']
})
export class DistrictPageComponent implements OnInit, AfterViewInit {
  @Input() currentDistrict: number = 0;
  @Output() backClick = new EventEmitter();
  @Output() quarterEndTriggered = new EventEmitter<{
    success: boolean,
    correctSelections: number,
    incorrectIds: string[]
  }>();
  @Output() selectedDistrict = new EventEmitter<number>();
  @Output() navigateToShop = new EventEmitter<string>();
  @ViewChild('swiper') swiper!: ElementRef<any>;

  shops: {[districtId: number]: ShopDisplay[]} = {};
  game_state: GameState | null = null;

  districtMap = {
    "University": {
      id: 0,
      color: '#FF8100'
    },
    "Retail": {
      id: 1,
      color: '#00D6E0'
    },
    "Suburban": {
      id: 2,
      color: '#00D398'
    },
    "Industrial": {
      id: 3,
      color: '#EA4E6C'
    },
    "Shopping Centre": {
      id: 4,
      color: '#F15B3D'
    },
  }

  getDistrictKeys() {
    return Object.keys(this.districtMap);
  }

  constructor(
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.dataService.gameState$.subscribe(gameState => {
      this.game_state = gameState;
      setTimeout(() => this.initializeShops());
    })
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeSwiper(), 100);
  }

  initializeShops() {
    this.shops = {};
    const allShops = this.game_state?.stores || {};
    
    // Predefined positions for shops in each district
    const POSITIONS = [
      /* University */ [
        { top: 10, left: 17 },
        { top: 23, left: 25.5 },
        { top: 38, left: 44 },
        { top: 2, left: 27.5 },
        { top: 8, left: 50 },
        { top: 27, left: 61 }
      ],
      /*   Retail   */ [
        { top: 40, left: 51 },
        { top: 27, left: 48 },
        { top: 17, left: 51 },
        { top: 19, left: 69 },
        { top: 15, left: 16 },
        { top: 30, left: 29 }
      ],
      /*  Suburban  */ [
        { top: 20, left: 20 },
        { top: 7, left: 46 },
        { top: 18, left: 73 },
        { top: 24.5, left: 45.5 },
        { top: 39.5, left: 46.5 },
        { top: 32.5, left: 26.5 }
      ],
      /* Industrial */ [
        { top: 20, left: 24 },
        { top: 23, left: 46 },
        { top: 8, left: 38 },
        { top: 29, left: 62 },
        { top: 13, left: 62 },
        { top: 8, left: 54 }
      ],
      /*  Shopping  */ [
        { top: 38, left: 34 },
        { top: 25, left: 13 },
        { top: 14, left: 39 },
        { top: 32, left: 54.5 },
        { top: 24.5, left: 51 },
        { top: 20, left: 71.5 }
      ]
    ];
  
    Object.entries(allShops).forEach(([shopId, shop], index) => {
      const districtId = this.districtMap[shop.district].id;
      
      if (!this.shops[districtId]) {
        this.shops[districtId] = [];
      }
  
      const position = this.shops[districtId].length;
      const quarter = this.game_state.game_state;
      const prevQuarter = 'q' + (Number(quarter.slice(1, 2)) - 1);
      let yieldStatus = 'neutral';  // 'positive', 'negative'
      if (quarter === 'q3' || quarter === 'q4') {
        if (shop.kpi[prevQuarter + '_start'].k2l > shop.kpi[quarter + '_start'].k2l) {
          yieldStatus = 'negative';
        } else if (shop.kpi[prevQuarter + '_start'].k2l < shop.kpi[quarter + '_start'].k2l) {
          yieldStatus = 'positive';
        }
      }
      
      this.shops[districtId].push({
        id: shopId,
        name: shop.name,
        type: shop.type,
        yieldStatus,
        ...POSITIONS[districtId][position],
        active: shop.state[this.game_state.game_state] !== 'inactive'
      });
    });
  }

  initializeSwiper() {
    const swiperParams = {
      updateOnWindowResize: true,
      initialSlide: this.currentDistrict
    };

    Object.assign(this.swiper.nativeElement, swiperParams);
    this.swiper.nativeElement.initialize();

    this.swiper.nativeElement.addEventListener('swiperslidechange', (event) => {
      this.currentDistrict = this.swiper.nativeElement.swiper.activeIndex;
      this.selectedDistrict.emit(this.currentDistrict);
    });
  }

  onBackClick(): void {
    this.backClick.emit();
  }

  onNavigateToShop(id: string) {
    this.navigateToShop.emit(id);
  }

  prevDistrict() {
    this.swiper.nativeElement.swiper.slidePrev();
  }

  nextDistrict() {
    this.swiper.nativeElement.swiper.slideNext();
  }
}
